import * as React from 'react';

import * as Images from 'pos/Images';
import { DiscountMode, NamedDiscountDTO, Permission, Res, TicketLineDTO } from 'core/api';
import { SessionProvider } from 'core/infrastructure';
import { CustomerDisplay } from 'pos/services';
import { getAuthorizerUserIdForDiscount } from 'pos/modules/helpers/named-discount-authorization';
import { Message } from 'core/ui-next/modal';
import * as SalesStore from '../SalesStore';

// Este fichero ya no pinta pantalla. Sólo exporta la funcionalidad de los descuentos con nombre de la línea
// (lo que antes era la clase SetTicketLineNamedDiscountScreen) migrada a un hook, para que la use
// setTicketLineDiscountScreen en la pestaña "Descuentos".

// Descuento usado para pintar el botón de eliminar descuento
export const REMOVE_DISCOUNT: NamedDiscountDTO = {
	Id: -182,
	Name: Res.Msg.MSG_REMOVE_NAMED_DISCOUNT(),
	Code: '{360E9548-E665-4BBD-8195-BA01AD28143F}',
	RequiresAuthorization: false,
	RequiresAuthorizerAuthentication: false,
	IsAllowed: true,
	Mode: DiscountMode.Rate,
	RateOrCash: 0,
	Style: {
		ImageId: Images.DB_REMOVE_DISCOUNT,
		BackColor: '',
		Text: Res.Msg.MSG_REMOVE_NAMED_DISCOUNT()
	}
};

export type TicketLineNamedDiscountParams = {
	line: TicketLineDTO;
	// Los descuentos con nombre de la línea (sin el botón de quitar descuento), que hacen falta para saber si se
	// puede quitar el descuento
	discounts: NamedDiscountDTO[];
	// La pantalla nos pasa el close del Modal para poder cerrarla igual que hacía el this.close() de la clase
	close: () => void;
};

export type TicketLineNamedDiscount = {
	applyDiscount: (discount: NamedDiscountDTO) => Promise<void>;
};

export function useTicketLineNamedDiscount({ line, discounts, close }: TicketLineNamedDiscountParams): TicketLineNamedDiscount {

	const setDiscount = React.useCallback(async (discount: NamedDiscountDTO) => {

		if (!discount.IsAllowed && !discount.RequiresAuthorization) {
			await Message.show(Res.Msg.MSG_HAVE_NOT_PERMISSION_TO_APPLY_DISCOUNT(discount.Name));
			return;
		}

		const result = await getAuthorizerUserIdForDiscount({ discount, ticketLineId: line.Id, getProposal: SalesStore.getNamedDiscountProposal });
		if (!result.ok)
			return;

		await SalesStore.setLineNamedDiscount(line, discount.Id, result.value);

		const updatedLine = SalesStore.getLineById(line.Id);
		if (!updatedLine)
			return;

		if (updatedLine.AppliedDiscountMode !== null && updatedLine.AppliedDiscountMode === DiscountMode.Cash)
			await CustomerDisplay.displayLineCashDiscount(SalesStore.getTicket(), updatedLine);
		else if (updatedLine.AppliedDiscountMode !== null && updatedLine.AppliedDiscountMode === DiscountMode.Rate)
			await CustomerDisplay.displayLineDiscountPercentage(SalesStore.getTicket(), updatedLine);

	}, [line]);

	const removeDiscount = React.useCallback(async () => {

		const canRemove = SessionProvider.hasPermission(Permission.Ticket_SetTicketLinePercentageDiscount)
			|| discounts.some(x => x.IsAllowed);

		if (!canRemove) {
			await Message.show(Res.Msg.MSG_HAVE_NOT_PERMISSION_TO_REMOVE_DISCOUNT());
			return;
		}

		await SalesStore.setLineDiscounts(line, 0, 0);
		await CustomerDisplay.displayExistingLine(SalesStore.getTicket(), SalesStore.getTicket().Lines.last());

	}, [discounts, line]);

	// Es el onSelectNamedDiscount de la clase antigua: se cierra la pantalla en cualquier caso, igual que hacía ella
	const applyDiscount = React.useCallback(async (discount: NamedDiscountDTO) => {

		if (discount.Id === REMOVE_DISCOUNT.Id)
			await removeDiscount();
		else
			await setDiscount(discount);

		close();

	}, [close, removeDiscount, setDiscount]);

	return { applyDiscount };
}